import React, { useEffect, useState, ReactNode } from "react";
import {
  Card,
  Row,
  Col,
  notification,
  Typography,
  Tag,
  Descriptions,
  Button,
  Spin,
  Modal,
  Input,
  Space,
  Alert,
  Tooltip,
} from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import {
  FinetuneRunNew,
  FinetuneDataset,
  ValidationRun,
} from "../../../model_gen/finetuning";
import {
  EditOutlined,
  DeploymentUnitOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { ValidationService } from "../../../services/ValidationService";
import ValidationRunTable from "../Validation/ValidationRunTable";

const FinetuneRunDetail: React.FC = () => {
  const { finetuneRunId } = useParams();
  const { apiKey } = usePermissions();
  const [finetuneRun, setFinetuneRun] = useState<FinetuneRunNew | null>(null);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDatasetLoading, setIsDatasetLoading] = useState<boolean>(false);
  const [isDeployModalVisible, setIsDeployModalVisible] =
    useState<boolean>(false);
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [modelName, setModelName] = useState<string>("");
  const [modelNameError, setModelNameError] = useState<string | null>(null);
  const [validationRuns, setValidationRuns] = useState<ValidationRun[]>([]);
  const [isValidationLoading, setIsValidationLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (finetuneRunId) {
      setIsLoading(true);
      FinetuneRunService.getFinetuneRunById(
        Number(finetuneRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: FinetuneRunNew) => {
          setFinetuneRun(data);

          // Fetch dataset details if we have a dataset ID
          if (data.finetune_dataset_id) {
            setIsDatasetLoading(true);
            DatasetService.getDataset(
              data.finetune_dataset_id,
              setIsDatasetLoading,
              apiKey
            ).subscribe(
              (datasetData: FinetuneDataset) => {
                setDataset(datasetData);
              },
              (error: any) => {
                console.error("Failed to fetch dataset:", error);
                notification.error({
                  message: "Error",
                  description: `Failed to fetch dataset details: ${error}`,
                });
              }
            );
          }
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch finetune run: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [finetuneRunId, apiKey]);

  const loadValidationRuns = (showLoading: boolean = true) => {
    if (finetuneRunId) {
      ValidationService.getValidationRunsByFinetuneRunId(
        finetuneRunId,
        showLoading ? setIsValidationLoading : null,
        apiKey
      ).subscribe(
        (data: ValidationRun[]) => {
          setValidationRuns(data);
        },
        (error: any) => {
          console.error("Failed to fetch validation runs:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch validation runs: ${error}`,
          });
        }
      );
    }
  };
  useEffect(() => {
    loadValidationRuns(true);
  }, [finetuneRunId, apiKey]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      CREATED: "default",
      PENDING: "processing",
      RUNNING: "processing",
      COMPLETED: "success",
      FAILED: "error",
      CANCELLED: "warning",
    };
    return statusMap[status] || "default";
  };

  // Helper function to get display name
  const getDisplayName = () => {
    if (!finetuneRun) return "";
    return finetuneRun.display_name;
  };

  const showDeployModal = () => {
    // Get the display name and format it: replace spaces with underscores and make lowercase
    const displayName = (getDisplayName() as string) || "";
    const initialName = displayName.replace(/\s+/g, "_").toLowerCase();

    setModelName(initialName);
    validateModelName(initialName);
    setIsDeployModalVisible(true);
  };

  const handleDeployCancel = () => {
    if (isDeploying) {
      return; // Prevent closing the modal while deploying
    }
    setIsDeployModalVisible(false);
    setModelName("");
    setModelNameError(null);
  };

  const validateModelName = (name: string): boolean => {
    if (!name.trim()) {
      setModelNameError("Model name cannot be empty");
      return false;
    }

    if (name.length > 50) {
      setModelNameError("Model name cannot exceed 50 characters");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_\-:]+$/;
    if (!validNameRegex.test(name)) {
      setModelNameError(
        "Model name can only contain alphanumeric characters, underscores, hyphens, and colons"
      );
      return false;
    }

    setModelNameError(null);
    return true;
  };

  const handleDeploy = () => {
    if (!validateModelName(modelName)) {
      return;
    }

    setIsDeploying(true);

    FinetuneRunService.deployFinetuneRun(
      Number(finetuneRunId),
      modelName,
      setIsDeploying,
      apiKey
    ).subscribe(
      (run: FinetuneRunNew) => {
        notification.success({
          message: "Success",
          description: `Model "${modelName}" has been queued for deployment.`,
        });
        setFinetuneRun(run);
        setIsDeployModalVisible(false);
        setModelName("");
        setModelNameError(null);
        setIsDeploying(false);
      },
      (error: any) => {
        notification.error({
          message: "Error",
          description: `Failed to deploy model: ${error}`,
        });
        setIsDeploying(false);
      }
    );
  };

  // Check if the Deploy button should be shown
  const shouldShowDeployButton = () => {
    if (!finetuneRun || finetuneRun.status !== "COMPLETED") {
      return false;
    }

    const meta = finetuneRun.meta || {};
    const modelId = meta.model_id;
    const deploymentStatus = getDeploymentStatus();

    // Show deploy button if model_id is null and deployment status is null or FAILED
    return !modelId && (!deploymentStatus || deploymentStatus === "FAILED");
  };

  // Get deployment status color
  const getDeploymentStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      SCHEDULED: "processing",
      DEPLOYING: "processing",
      DEPLOYED: "success",
      FAILED: "error",
    };
    return statusMap[status] || "default";
  };

  // Helper to safely get deployment status as string
  const getDeploymentStatus = (): string | null => {
    if (!finetuneRun?.meta?.model_deployment_status) {
      return null;
    }
    return String(finetuneRun.meta.model_deployment_status);
  };

  // Check if deployment status should be displayed
  const shouldShowDeploymentStatus = (): boolean => {
    const status = getDeploymentStatus();
    return status !== null && status !== "FAILED";
  };

  if (isLoading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      />
    );
  }

  // Get the deployment status for display
  const deploymentStatus = getDeploymentStatus();

  return (
    <Card className="applications-container applications-wrapper">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
            <Link to="/models/finetuning" style={{ color: "#828282" }}>
              Finetune Runs
              {" / "}
            </Link>
            {getDisplayName() as ReactNode}
          </Typography>
        </Col>
        <Col>
          {finetuneRun && (
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/models/finetuning/${finetuneRunId}/edit`)
                }>
                Edit
              </Button>
              {shouldShowDeployButton() && (
                <Button
                  type="primary"
                  icon={<DeploymentUnitOutlined />}
                  onClick={showDeployModal}>
                  Deploy
                </Button>
              )}
            </Space>
          )}
        </Col>
      </Row>

      {/* Deploy Model Modal */}
      <Modal
        title="Deploy Finetuned Model"
        open={isDeployModalVisible}
        onCancel={handleDeployCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleDeployCancel}
            disabled={isDeploying}>
            Cancel
          </Button>,
          <Button
            key="deploy"
            type="primary"
            onClick={handleDeploy}
            loading={isDeploying}
            disabled={
              !modelName.trim() || modelNameError !== null || isDeploying
            }>
            Deploy
          </Button>,
        ]}>
        {isDeploying ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
            <p style={{ marginTop: "16px" }}>Deploying model...</p>
          </div>
        ) : (
          <>
            <p>Please provide a name for your deployed model:</p>
            <Input
              placeholder="Model name"
              value={modelName}
              onChange={(e) => {
                const value = e.target.value;
                setModelName(value);
                validateModelName(value);
              }}
              maxLength={50}
              status={modelNameError ? "error" : ""}
              style={{ marginBottom: 8 }}
            />
            {modelNameError && (
              <Alert
                message={modelNameError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        )}
      </Modal>

      {finetuneRun && (
        <>
          {/* Details Card */}
          <Card
            className="overview-wrapper"
            style={{ marginBottom: 24 }}
            bordered>
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <Descriptions colon={false} column={12} layout="vertical">
                  <Descriptions.Item label="Description" span={6}>
                    {finetuneRun.description || "No description provided"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date Created" span={3}>
                    {moment(finetuneRun.date_created).format(
                      "MMM D, YYYY HH:mm:ss"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status" span={3}>
                    <Tag color={getStatusColor(finetuneRun.status)}>
                      {finetuneRun.status}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col span={24}>
                <Descriptions colon={false} column={12} layout="vertical">
                  {(finetuneRun.application_name ||
                    finetuneRun.intent_name ||
                    finetuneRun.intent_group_name) && (
                    <Descriptions.Item
                      label="Data Source"
                      span={shouldShowDeploymentStatus() ? 3 : 4}>
                      {finetuneRun.intent_group_id ? (
                        <Link
                          to={`/intent-group/${finetuneRun.intent_group_id}`}>
                          {finetuneRun.intent_group_name}
                        </Link>
                      ) : finetuneRun.application_id &&
                        finetuneRun.intent_id ? (
                        <Link
                          to={`/application/${finetuneRun.application_id}/intent/${finetuneRun.intent_id}`}>
                          {`${finetuneRun.application_name} / ${finetuneRun.intent_name}`}
                        </Link>
                      ) : finetuneRun.application_name &&
                        finetuneRun.intent_name ? (
                        `${finetuneRun.application_name} / ${finetuneRun.intent_name}`
                      ) : (
                        finetuneRun.application_name ||
                        finetuneRun.intent_name ||
                        finetuneRun.intent_group_name
                      )}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label="Dataset"
                    span={shouldShowDeploymentStatus() ? 3 : 4}>
                    {isDatasetLoading ? (
                      <Spin size="small" />
                    ) : dataset ? (
                      <Link
                        to={`/models/datasets/${finetuneRun.finetune_dataset_id}`}>
                        {dataset.name}
                      </Link>
                    ) : finetuneRun.finetune_dataset_id ? (
                      <Link
                        to={`/models/datasets/${finetuneRun.finetune_dataset_id}`}>
                        Dataset
                      </Link>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Base Model"
                    span={shouldShowDeploymentStatus() ? 3 : 4}>
                    {finetuneRun.base_model}
                  </Descriptions.Item>
                  {shouldShowDeploymentStatus() && deploymentStatus && (
                    <Descriptions.Item label="Deployment Status" span={3}>
                      <Tag color={getDeploymentStatusColor(deploymentStatus)}>
                        {deploymentStatus}
                      </Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </Card>

          {/* Configuration Card */}
          <Card
            className="overview-wrapper"
            style={{ marginBottom: 24 }}
            bordered>
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <Descriptions
                  colon={false}
                  column={12}
                  layout="vertical"
                  title="Configuration">
                  <Descriptions.Item label="Conversation Mode" span={3}>
                    {finetuneRun.conversation_mode ? "Yes" : "No"}
                  </Descriptions.Item>
                  <Descriptions.Item label="4-bit Quantization" span={3}>
                    {finetuneRun.fourbit ? "Yes" : "No"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Remove Examples" span={3}>
                    {finetuneRun.remove_examples ? "Yes" : "No"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Run Version" span={3}>
                    {finetuneRun.run_version}
                  </Descriptions.Item>
                  <Descriptions.Item label="Remove Example Tag" span={3}>
                    {finetuneRun.remove_example_tag ? "Yes" : "No"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Remove Output Tag" span={3}>
                    {finetuneRun.remove_output_tag ? "Yes" : "No"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Warmup Steps" span={3}>
                    {finetuneRun.meta?.warmup_steps !== undefined
                      ? String(finetuneRun.meta.warmup_steps)
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Learning Rate" span={3}>
                    {finetuneRun.meta?.learning_rate !== undefined
                      ? String(finetuneRun.meta.learning_rate)
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </Card>

          {/* Prompt Card */}
          {finetuneRun.prompt && (
            <Card
              className="overview-wrapper"
              style={{ marginBottom: 24 }}
              bordered>
              <Descriptions colon={false} column={1} title="Prompt">
                <Descriptions.Item>
                  <pre
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {finetuneRun.prompt}
                  </pre>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Prompt Deletions Card */}
          {finetuneRun.prompt_deletions &&
            finetuneRun.prompt_deletions.length > 0 && (
              <Card
                className="overview-wrapper"
                style={{ marginBottom: 24 }}
                bordered>
                <Descriptions colon={false} column={1} title="Prompt Deletions">
                  <Descriptions.Item>
                    <ul>
                      {finetuneRun.prompt_deletions.map((deletion, index) => (
                        <li key={index}>{deletion}</li>
                      ))}
                    </ul>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
        </>
      )}

      {/* Validation Runs Card */}
      <Card className="overview-wrapper" bordered style={{ marginBottom: 24 }}>
        <Descriptions
          colon={false}
          column={1}
          title={
            <>
              Validation Runs{" "}
              <Tooltip
                title="Validation sets are part of the random train/test split and are automatically run as part of the model training process"
                placement="top">
                <InfoCircleOutlined style={{ fontSize: "14px" }} />
              </Tooltip>
            </>
          }>
          <Descriptions.Item>
            <ValidationRunTable
              validationRuns={validationRuns}
              isLoading={isValidationLoading}
              showCompany={false}
              onRefresh={() => loadValidationRuns(false)}
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Card>
  );
};

export default FinetuneRunDetail;
